import React, { useMemo } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Box } from '@mui/material';
import { sanitizeDischargeHtml } from '../../utils/sanitizeHtml';

interface DischargeSummaryEditorProps {
  content: string;
  onChange: (content: string) => void;
  readOnly?: boolean;
}

const DischargeSummaryEditor: React.FC<DischargeSummaryEditorProps> = ({
  content,
  onChange,
  readOnly = false,
}) => {
  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'align': [] }],
      ['clean']
    ],
  };

  const formats = [
    'header',
    'bold', 'italic', 'underline',
    'list', 'bullet',
    'align'
  ];

  // In read-only (view) mode, sanitize the stored content before ReactQuill
  // parses it via innerHTML — this is the XSS-VULN-01 sink, and the value is
  // stable here so there's no editing/cursor interaction. In edit mode the
  // content is seeded already-sanitized (see DischargeSummarySimplified), so it
  // is passed through unchanged to avoid re-sanitizing on every keystroke.
  const value = useMemo(
    () => (readOnly ? sanitizeDischargeHtml(content) : content || ''),
    [content, readOnly],
  );

  return (
    <>
      <style type="text/css" media="print">
        {`
          @media print {
            /* Hide all UI elements except the discharge summary content */
            body * {
              visibility: hidden;
            }
            .ql-container, .ql-container * {
              visibility: visible;
            }
            .ql-container {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
            }
            /* Hide toolbar, buttons, navigation, headers */
            .ql-toolbar,
            button,
            .MuiButton-root,
            header,
            nav,
            .MuiPaper-root > div:first-child,
            [class*="MuiAppBar"],
            [class*="MuiDrawer"] {
              display: none !important;
              visibility: hidden !important;
            }
            /* Style the content for print */
            .ql-editor {
              padding: 0 !important;
              min-height: auto !important;
            }
          }
        `}
      </style>
      <Box sx={{ 
        '.ql-container': { 
          minHeight: '600px',
          fontSize: '14px',
          fontFamily: '"Times New Roman", serif'
        },
        '.ql-editor': {
          minHeight: '600px',
        }
      }}>
        <ReactQuill
          theme="snow"
          value={value}
          onChange={onChange}
          readOnly={readOnly}
          modules={readOnly ? { toolbar: false } : modules}
          formats={formats}
          placeholder="Discharge summary content will appear here..."
        />
      </Box>
    </>
  );
};

export default DischargeSummaryEditor;

